/** Angular */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
/** Sixense */
import { BydGenerateParams, BydModel } from '@sixense/byd-core-service';
/** Services */
import { BydRhsAbstractService } from '../../../../shared/services/byd-rhs-abstract.service';
/** Models */
import { InstanceFilterInterface, InstanceFilterModel } from '@sixense/ds-instances-models';
/** Constants */
import { DS_FORMS_BASE_URL } from '../../constants/ds-forms-base-url.constant';
import { PageModel } from '../../../../shared/models/page.model';
import { InstanceFilterQueryModel } from '../../models/instance-filter-query.model';

@Injectable()
export class DsFormsFiltersService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Get all shared filters or created by the user.
   *
   * @param projectId - Id of the project
   * @param pageInstanceFilters - PageModel that contains elementPerPage & pageIndex.
   * @param filters - Instance filter filters object.
   * @returns - Observable<BydModel<InstanceFilterModel>>
   */
  public getFilters(
    projectId: number,
    pageInstanceFilters?: PageModel,
    filters?: InstanceFilterQueryModel
  ): Observable<BydModel<InstanceFilterModel>> {
    this.path = `${DS_FORMS_BASE_URL}${projectId}/instances/filters/page`;
    const headers = pageInstanceFilters
      ? this.bydRhsHeadersService.setHeadersWithPagination(
          pageInstanceFilters.elementsPerPage,
          pageInstanceFilters.pageIndex
        )
      : this.bydRhsHeadersService.setHeadersWithPagination();
    const params = BydGenerateParams(filters);
    return this.getModels<InstanceFilterModel>({ headers, params });
  }

  /**
   * Add a new form filter.
   *
   * @param projectId the project id
   * @param payload Payload to create the filter
   * @returns Observable<InstanceFilterModel>
   */
  public addFilter(projectId: number, payload: InstanceFilterModel): Observable<InstanceFilterModel> {
    this.path = `${DS_FORMS_BASE_URL}${projectId}/instances/filters`;
    return this.postModel<InstanceFilterModel>({ body: payload });
  }

  /**
   * Update a form filter.
   *
   * @param projectId the project id
   * @param filterId Filter id
   * @param payload Payload to create the filter
   * @returns Observable<InstanceFilterModel>
   */
  public updateFilter(
    projectId: number,
    filterId: number,
    payload: InstanceFilterModel
  ): Observable<InstanceFilterModel> {
    this.path = `${DS_FORMS_BASE_URL}${projectId}/instances/filters/${filterId}`;
    return this.putModel<InstanceFilterModel>({ body: payload });
  }

  /**
   * Delete a form filter.
   *
   * @param projectId the project id
   * @param filterId Filter id
   * @param payload Payload to create the filter
   * @returns Observable<boolean>
   */
  public deleteFilter(projectId: number, filterId: number): Observable<boolean> {
    this.path = `${DS_FORMS_BASE_URL}${projectId}/instances/filters/${filterId}`;
    return this.delete();
  }

  /**
   * Share/unshare a form filter.
   *
   * @param projectId the project id
   * @param filterId Filter id
   * @param shared The shared state to set
   * @returns Observable<InstanceFilterModel>
   */
  public shareFilter(projectId: number, filterId: number, shared: boolean): Observable<InstanceFilterModel> {
    this.path = `${DS_FORMS_BASE_URL}${projectId}/instances/filters/${filterId}/share`;
    return this.putModel<InstanceFilterModel>({ body: { shared } });
  }

  /**
   * Create InstanceFilterModel instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `InstanceFilterModel` instance.
   */
  public createNewInstance(jsonObject: InstanceFilterInterface): InstanceFilterModel {
    return new InstanceFilterModel(jsonObject);
  }
}
